/**
 * VoiceInputButton - Reusable Speech-to-Text Component
 * 
 * Uses Web Speech API for voice recognition with:
 * - Auto-restart when browser times out (continues until user stops)
 * - Real-time interim results display
 * - Better error handling
 * Does NOT auto-send - returns transcribed text to parent.
 */

import { useState, useRef, useEffect, useCallback } from 'react';
import { Button } from './ui/button';
import { Mic, MicOff, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const VoiceInputButton = ({
    onTranscript,
    onInterimTranscript, // Optional: for showing real-time preview
    disabled = false,
    className = '',
    size = 'sm',
    showUnsupportedMessage = true
}) => {
    const [isListening, setIsListening] = useState(false);
    const [isSupported, setIsSupported] = useState(false);
    const [isProcessing, setIsProcessing] = useState(false); // Shows when processing audio
    const recognitionRef = useRef(null);
    const shouldRestartRef = useRef(false); // Track if we want to auto-restart
    const accumulatedTextRef = useRef(''); // Accumulate text across restarts

    useEffect(() => {
        // Check for Web Speech API support
        const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
        if (SpeechRecognition) {
            setIsSupported(true);
            const recognition = new SpeechRecognition();
            recognition.continuous = true;
            recognition.interimResults = true;
            recognition.lang = 'en-US';
            recognition.maxAlternatives = 1;

            recognition.onresult = (event) => {
                let finalTranscript = '';
                let interimTranscript = '';

                for (let i = event.resultIndex; i < event.results.length; i++) {
                    const result = event.results[i];
                    const text = result[0].transcript;
                    if (result.isFinal) {
                        finalTranscript += text;
                    } else {
                        interimTranscript += text;
                    }
                }

                // Show interim results in real-time
                if (interimTranscript && onInterimTranscript) {
                    onInterimTranscript(interimTranscript);
                    setIsProcessing(true);
                }

                // Send final results
                if (finalTranscript) {
                    accumulatedTextRef.current += (accumulatedTextRef.current ? ' ' : '') + finalTranscript.trim();
                    if (onTranscript) {
                        onTranscript(finalTranscript.trim());
                    }
                    setIsProcessing(false);
                }
            };

            recognition.onerror = (event) => {
                console.error('Speech recognition error:', event.error);
                setIsProcessing(false);

                // Don't show error for 'no-speech' - just restart
                if (event.error === 'no-speech') {
                    // Browser detected no speech, will restart if shouldRestart is true
                    return;
                }

                if (event.error === 'not-allowed') {
                    shouldRestartRef.current = false;
                    setIsListening(false);
                    toast.error('Microphone access denied. Please allow microphone access in your browser settings.');
                } else if (event.error === 'network') {
                    toast.error('Network error. Speech recognition requires internet connection.');
                } else if (event.error === 'audio-capture') {
                    shouldRestartRef.current = false;
                    setIsListening(false);
                    toast.error('No microphone found. Please connect a microphone.');
                } else if (event.error !== 'aborted') {
                    // Don't show error for intentional abort
                    toast.warning(`Speech recognition: ${event.error}`);
                }
            };

            recognition.onend = () => {
                setIsProcessing(false);
                // Auto-restart if user hasn't explicitly stopped
                if (shouldRestartRef.current) {
                    try {
                        setTimeout(() => {
                            if (shouldRestartRef.current && recognitionRef.current) {
                                recognitionRef.current.start();
                            }
                        }, 100);
                    } catch (e) {
                        console.error('Failed to restart recognition:', e);
                        shouldRestartRef.current = false;
                        setIsListening(false);
                    }
                } else {
                    setIsListening(false);
                }
            };

            recognition.onaudiostart = () => {
                setIsProcessing(true);
            };

            recognitionRef.current = recognition;
        }

        return () => {
            shouldRestartRef.current = false;
            if (recognitionRef.current) {
                recognitionRef.current.abort();
            }
        };
    }, [onTranscript, onInterimTranscript]);

    const toggleListening = useCallback(() => {
        if (!recognitionRef.current) {
            toast.error('Speech recognition not available in this browser');
            return;
        }

        if (isListening) {
            // User wants to stop
            shouldRestartRef.current = false;
            recognitionRef.current.stop();
            setIsListening(false);
            setIsProcessing(false);
            accumulatedTextRef.current = '';
        } else {
            // User wants to start
            try {
                shouldRestartRef.current = true;
                accumulatedTextRef.current = '';
                recognitionRef.current.start();
                setIsListening(true);
                toast.success('Listening... Click again to stop.', { duration: 2000 });
            } catch (e) {
                console.error('Failed to start recognition:', e);
                shouldRestartRef.current = false;
                toast.error('Could not start voice input. Try again.');
            }
        }
    }, [isListening]);

    if (!isSupported) {
        if (!showUnsupportedMessage) {
            return null;
        }
        return (
            <Button
                variant="ghost"
                size={size}
                disabled
                className={`text-slate-600 cursor-not-allowed ${className}`}
                title="Voice input not supported in this browser (try Chrome or Edge)"
            >
                <Mic className="h-5 w-5" />
            </Button>
        );
    }

    return (
        <Button
            variant="ghost"
            size={size}
            onClick={toggleListening}
            disabled={disabled}
            className={`transition-all ${isListening
                ? 'text-red-400 bg-red-500/20 hover:bg-red-500/30'
                : 'text-slate-400 hover:text-orange-400 hover:bg-slate-700/50'
                } ${className}`}
            title={isListening ? 'Click to stop listening' : 'Click to start voice input'}
        >
            {isListening ? (
                isProcessing ? (
                    <Loader2 className="h-5 w-5 animate-spin" />
                ) : (
                    <MicOff className="h-5 w-5 animate-pulse" />
                )
            ) : (
                <Mic className="h-5 w-5" />
            )}
        </Button>
    );
};

// Hook version for more control
export const useVoiceInput = () => {
    const [isListening, setIsListening] = useState(false);
    const [transcript, setTranscript] = useState('');
    const [interimTranscript, setInterimTranscript] = useState('');
    const [isSupported, setIsSupported] = useState(false);
    const recognitionRef = useRef(null);
    const shouldRestartRef = useRef(false);

    useEffect(() => {
        const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
        if (SpeechRecognition) {
            setIsSupported(true);
            const recognition = new SpeechRecognition();
            recognition.continuous = true;
            recognition.interimResults = true;
            recognition.lang = 'en-US';

            recognition.onresult = (event) => {
                let finalTranscript = '';
                let interim = '';

                for (let i = event.resultIndex; i < event.results.length; i++) {
                    const result = event.results[i];
                    const text = result[0].transcript;
                    if (result.isFinal) {
                        finalTranscript += text;
                    } else {
                        interim += text;
                    }
                }

                // Update interim display
                setInterimTranscript(interim);

                // Accumulate final text
                if (finalTranscript) {
                    setTranscript(prev => prev + (prev ? ' ' : '') + finalTranscript.trim());
                    setInterimTranscript('');
                }
            };

            recognition.onerror = (event) => {
                console.error('Speech recognition error:', event.error);
                if (event.error === 'not-allowed' || event.error === 'audio-capture') {
                    shouldRestartRef.current = false;
                    setIsListening(false);
                }
                // Ignore 'no-speech' - will auto-restart
            };

            recognition.onend = () => {
                // Auto-restart if user hasn't stopped
                if (shouldRestartRef.current) {
                    setTimeout(() => {
                        if (shouldRestartRef.current && recognitionRef.current) {
                            try {
                                recognitionRef.current.start();
                            } catch (e) {
                                shouldRestartRef.current = false;
                                setIsListening(false);
                            }
                        }
                    }, 100);
                } else {
                    setIsListening(false);
                }
            };

            recognitionRef.current = recognition;
        }

        return () => {
            shouldRestartRef.current = false;
            if (recognitionRef.current) {
                recognitionRef.current.abort();
            }
        };
    }, []);

    const startListening = useCallback(() => {
        if (!recognitionRef.current) return;
        setTranscript('');
        setInterimTranscript('');
        shouldRestartRef.current = true;
        try {
            recognitionRef.current.start();
            setIsListening(true);
        } catch (e) {
            console.error('Failed to start:', e);
            shouldRestartRef.current = false;
        }
    }, []);

    const stopListening = useCallback(() => {
        if (!recognitionRef.current) return;
        shouldRestartRef.current = false;
        recognitionRef.current.stop();
        setIsListening(false);
        setInterimTranscript('');
    }, []);

    const toggleListening = useCallback(() => {
        if (isListening) {
            stopListening();
        } else {
            startListening();
        }
    }, [isListening, startListening, stopListening]);

    const clearTranscript = useCallback(() => {
        setTranscript('');
        setInterimTranscript('');
    }, []);

    return {
        isListening,
        transcript,
        interimTranscript, // Real-time preview
        isSupported,
        startListening,
        stopListening,
        toggleListening,
        clearTranscript
    };
};

export default VoiceInputButton;
